<!-- components/showProcedure.vue -->
<template>
  <card v-if="Object.keys(sourceProcedure).length > 0" class="flex flex-col gap-6 h-fit" padding="p-5">
    <div v-if="!canSee" class="p-4 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-500">
      Você não tem permissão para ver o conteúdo deste procedimento.
    </div>

    <template v-else>
      <div class="flex flex-col gap-2">
        <span class="line gap-x-2 font-bold">
          {{ sourceProcedure.name }}

          <rpm-button
              v-if="canEdit"
              icon="fa-solid fa-pen-to-square"
              tooltip-text="editar"
              color="transparent"
              @click="$emit('openProcedureModal', sourceProcedure)"
          />
        </span>

        <div class="flex flex-row gap-2">
          <badge class="bg-sky-400">
            ID: {{ sourceProcedure.id }}
          </badge>
          <badge :class="sourceProcedure.enabled ? 'bg-green-500':'bg-red-500'">
            {{ sourceProcedure.enabled ? 'Ativo' : 'Inativo' }}
          </badge>
        </div>

        <div class="flex flex-row gap-2 items-center">
          <i class="fa-solid fa-calendar-day"></i>
          <b>Prazo:</b>
          {{ formatDeadLine(sourceProcedure.execution_deadline) }}
        </div>

        <div class="grid grid-cols-[auto_1fr] gap-2">
          <i class="fa-solid fa-circle-info mt-1"></i>
          <span v-html="sourceProcedure.description"></span>
        </div>
      </div>

      <!-- ---------------- Setores ---------------- -->
      <div class="flex flex-col gap-1">
        <div class="flex flex-row gap-2 items-center">
          <i class="fa-solid fa-building"></i>
          <b>Setores</b>
        </div>

        <rpm-table
            no-card
            :columns="[{ field: 'name', label: 'setor' }]"
            :data="sourceProcedure.organizations"
        >
          <template #actions="{ item }">
            <rpm-button
                v-if="canEdit"
                icon="fa-solid fa-pen-to-square"
                tooltip-text="editar"
                color="transparent"
                @click="openEditOrganization(item)"
            />
            <rpm-button
                v-if="canEdit"
                color-class="text-red-500 hover:text-red-600 focus:text-red-600"
                font-class="text-lg"
                icon="fa-solid fa-trash"
                tooltip-text="excluir"
                class="h-6 w-6 rounded-md"
                @click="deleteOrganization(item)"
            />
          </template>

          <template #footer>
            <td>
              <div class="flex flex-row justify-center py-2">
                <rpm-button
                    v-if="canEdit"
                    icon="fa-solid fa-plus"
                    tooltip-text="adicionar"
                    class="px-2 py-1 rounded-md"
                    font-class="text-sm"
                    @click="$emit('openCreateOrganizationModal', {})"
                />
              </div>
            </td>
          </template>
        </rpm-table>
      </div>

      <!-- ---------------- Situações ---------------- -->
      <div class="flex flex-col gap-1">
        <div class="flex flex-row gap-2 items-center">
          <i class="fa-solid fa-link"></i>
          <b>Situações</b>
        </div>

        <rpm-table
            no-card
            :columns="[
            { field: 'main', label: 'situação' },
            { field: 'next_source_procedure.name', label: 'próximo procedimento' }
          ]"
            :data="sourceProcedure.situations"
        >
          <template #cell:main="{ item: situation }">
            <div class="flex flex-col gap-1">
              <div class="flex flex-row gap-1">
                <badge class="bg-sky-400">ID: {{ situation.id }}</badge>
                <badge :class="situation.enabled ? 'bg-green-500':'bg-red-500'">
                  {{ situation.enabled_message }}
                </badge>
              </div>
              <span>{{ situation.source_situation.description }}</span>
              <span class="text-sm text-default/80 dark:text-white/80">
                {{ situation.observation_required ? "Requer observação" : "Não requer observação" }}
              </span>
            </div>
          </template>

          <template #actions="{ item }">
            <rpm-button
                v-if="canEdit"
                icon="fa-solid fa-pen-to-square"
                tooltip-text="editar"
                color="transparent"
                @click="$emit('openEditSituationModal', item)"
            />
            <rpm-button
                v-if="canEdit"
                color-class="text-red-500 hover:text-red-600 focus:text-red-600"
                font-class="text-lg"
                icon="fa-solid fa-trash"
                tooltip-text="excluir"
                class="h-6 w-6 rounded-md"
                @click="openDeleteModal(item)"
            />
          </template>

          <template #footer>
            <td>
              <div class="flex flex-row justify-center py-2">
                <rpm-button
                    v-if="canEdit"
                    icon="fa-solid fa-plus"
                    tooltip-text="adicionar"
                    class="px-3 py-1.5 rounded-md"
                    font-class="text-md"
                    @click="$emit('openCreateSituationModal', true)"
                />
              </div>
            </td>
          </template>
        </rpm-table>

        <questionnaire
            :questionnaire="sourceProcedure.questionnaire"
            :sourceProcedure="sourceProcedure"
            :can-execute="canExecute"
            :can-edit="canEdit"
            @change-questionnaire="(questionnaire) => { sourceProcedure.questionnaire = questionnaire }"
            @all-questionnaires-deleted="handleAllQuestionnairesDeleted"
        />
      </div>

      <delete-modal-situation
          :open="deleteModalOpen"
          :situation="situationToDelete"
          @close="deleteModalOpen = false"
          @canceled="handleSituationCanceled"
      />
    </template>
  </card>
</template>

<script>
import Questionnaire from "./questionnaire";
import RpmSpinner from "#components/RpmSpinner.vue";
import RpmTable from "#components/RpmTable.vue";
import Badge from "#components/Badge.vue";
import DeleteModalSituation from "../modals/deleteModalSituation.vue";

import sourceServiceProcedureOrganizationRepository
  from '#repositories/sourceServiceProcedureOrganizationRepository';
import sourceProcedureSituationRepository from '#repositories/sourceProcedureSituationRepository';

export default {
  name: "showProcedure",
  components: {
    Questionnaire,
    RpmSpinner,
    RpmTable,
    Badge,
    DeleteModalSituation
  },
  emits: [
    'openProcedureModal',
    'openCreateOrganizationModal',
    'openEditOrganizationModal',
    'openCreateSituationModal',
    'openEditSituationModal'
  ],
  props: {
    sourceProcedure: { type: Object, default: null }
  },
  data() {
    return {
      loading: true,
      deleteModalOpen: false,
      situationToDelete: null,
      actionsCatalog: []
    };
  },
  computed: {
    actionsObj() {
      return this.sourceProcedure?.actions ?? { see: false, execute: false, edit: false };
    },
    canSee()    { return !!this.actionsObj.see; },
    canExecute(){ return !!this.actionsObj.execute; },
    canEdit()   { return !!this.actionsObj.edit; },
  },
  async mounted() {
    this.loading = false;
  },
  methods: {
    formatDeadLine(deadline) {
      deadline = parseInt(deadline);
      const time = deadline > 0 ? deadline : "Imediato";
      return deadline <= 0 ? time : time + (deadline > 1 ? " dias" : " dia");
    },

    async refreshOrganizations() {
      try {
        const spId = this.sourceProcedure?.id;
        if (!spId) return;
        const { data } = await sourceServiceProcedureOrganizationRepository
            .getSourceProcedureOrganizations(spId);

        this.sourceProcedure.organizations = Array.isArray(data?.data) ? data.data : (data ?? []);
      } catch (_) {}
    },

    async loadActions() {
      try {
      } catch (_) {}
    },

    async onCreateOrganization(payload) {
      if (!this.canEdit) { this.$notify('Você não tem permissão para editar este procedimento.', 'error'); return; }
      await this._upsertOrganization(payload, { mode: 'create' });
    },
    async onEditOrganization(payload) {
      if (!this.canEdit) { this.$notify('Você não tem permissão para editar este procedimento.', 'error'); return; }
      await this._upsertOrganization(payload, { mode: 'edit' });
    },


    async _upsertOrganization(rawPayload, { mode }) {
      const spId = this.sourceProcedure?.id;
      if (!spId) {
        this.$notify('Procedimento inválido.', 'error');
        return;
      }

      try {
        let actions = rawPayload?.actions || [];
        if (Array.isArray(actions) && actions.length && typeof actions[0] === 'object') {
          actions = actions.map(a => a.id);
        }
        actions = actions.map(n => Number(n));

        const required_owner = ('' + rawPayload?.required_owner) === 'true' || rawPayload?.required_owner === true;

        const payload = {
          organization_id: Number(rawPayload.organization_id),
          actions,
          required_owner
        };

        if (!payload.organization_id || !Array.isArray(payload.actions)) {
          this.$notify('Dados inválidos para salvar setor.', 'error');
          return;
        }

        await sourceServiceProcedureOrganizationRepository
            .updateSourceProcedureOrganization(spId, payload);

        await this.refreshOrganizations();

        this.$notify(
            mode === 'create' ? 'Setor adicionado ao procedimento.' : 'Setor atualizado no procedimento.',
            'success'
        );
      } catch (e) {
        this.$notify('Erro ao salvar o setor no procedimento.', 'error');
      }
    },

    async openEditOrganization(item) {
      if (!this.canEdit) { this.$notify('Você não tem permissão para editar este procedimento.', 'error'); return; }

      const spId = this.sourceProcedure?.id;
      const orgId = item?.id;
      if (!spId || !orgId) {
        this.$notify('Dados insuficientes para editar o setor.', 'error');
        return;
      }

      try {
        const { data } = await sourceServiceProcedureOrganizationRepository
            .findByOrganizationIdAndSourceProcedureId({
              organization_id: orgId,
              source_procedure_id: spId,
            });

        const details = data?.data ?? data ?? {};
        let actionIds = details.actions || item.actions || [];
        if (Array.isArray(actionIds) && actionIds.length && typeof actionIds[0] === 'object') {
          actionIds = actionIds.map(a => a.id);
        } else {
          actionIds = actionIds.map(a => Number(a));
        }
        const requiredOwner = ('' + (details.required_owner ?? item.required_owner ?? false)) === 'true';

        this.$emit('openEditOrganizationModal', {
          organization_id: orgId,
          organization_name: item.name,
          actions: actionIds,
          required_owner: requiredOwner
        });
      } catch (e) {
        this.$notify('Não foi possível carregar as permissões deste setor.', 'error');
      }
    },

    async deleteOrganization(item) {
      if (!this.canEdit) {
        this.$notify('Você não tem permissão para editar este procedimento.', 'error');
        return;
      }

      const spId = this.sourceProcedure?.id;
      const orgId = item?.id;

      if (!spId || !orgId) {
        this.$notify('Dados insuficientes para excluir o setor.', 'error');
        return;
      }

      const previous = [...this.sourceProcedure.organizations];
      this.sourceProcedure.organizations = previous.filter(org => org.id !== orgId);

      try {
        await sourceServiceProcedureOrganizationRepository
            .deleteSourceProcedureOrganization(spId, orgId);

        this.$notify('Setor removido do procedimento.', 'success');
      } catch (e) {
        this.sourceProcedure.organizations = previous;
        this.$notify('Erro ao remover o setor.', 'error');
      }
    },

    openDeleteModal(situation) {
      if (!this.canEdit) {
        this.$notify('Você não tem permissão para editar esta situação.', 'error');
        return;
      }
      this.situationToDelete = situation;
      this.deleteModalOpen = true;
    },
    handleSituationCanceled() {
      this.sourceProcedure.situations = this.sourceProcedure.situations.filter(
          sit => sit.id !== this.situationToDelete.id
      );
      this.deleteModalOpen = false;
    },
    handleAllQuestionnairesDeleted() {
      this.sourceProcedure.questionnaire = null;
    }
  }
};
</script>
